import { InfoCard, Link } from '@backstage/core-components';
import { fetchApiRef, useApiHolder } from '@backstage/core-plugin-api';
import { techdocsStorageApiRef } from '@backstage/plugin-techdocs-react';
import { Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useHomeCardFooterStyles } from './HomeCardFooter';

export const RELEASE_DOCS_ENTITY = { namespace: 'default', kind: 'component', name: 'pt-release-notes' };
export const RELEASE_DOCS_PATH = '/docs/default/component/pt-release-notes/';

/**
 * Explicit publication intent takes priority. Otherwise the source repository's
 * FY/Q/Sprint convention is compared numerically, never by navigation order.
 */
export function latestPublishedReleasePath(html: string, origin: string, locations: string[] = []): string | undefined {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const paths = new Set<string>();
  for (const link of Array.from(doc.querySelectorAll('a[href]'))) {
    const label = (link.textContent ?? '').replace(/\s+/g, ' ').trim();
    const explicit = (link.getAttribute('rel') ?? '').split(/\s+/).includes('latest') || /^(?:view |read |our )?latest release notes$/i.test(label);
    if (!explicit) continue;
    const href = link.getAttribute('href')!;
    if (!href || href.startsWith('#')) continue;
    let url: URL;
    try {
      url = new URL(href, `${origin}${RELEASE_DOCS_PATH}`);
    } catch {
      continue;
    }
    if (url.origin !== origin || !url.pathname.startsWith(RELEASE_DOCS_PATH) || url.pathname === RELEASE_DOCS_PATH || /%2f|%5c/i.test(url.pathname))
      continue;
    paths.add(`${url.pathname}${url.search}${url.hash}`);
  }
  if (paths.size) return paths.size === 1 ? Array.from(paths)[0] : undefined;
  const candidates = [...Array.from(doc.querySelectorAll('a[href]')).map(link => link.getAttribute('href')!), ...locations];
  let newest: { rank: number[]; path: string } | undefined;
  for (const href of candidates) {
    let url: URL;
    try {
      url = new URL(href, `${origin}${RELEASE_DOCS_PATH}`);
    } catch {
      continue;
    }
    if (url.origin !== origin || !url.pathname.startsWith(RELEASE_DOCS_PATH)) continue;
    const relative = url.pathname.slice(RELEASE_DOCS_PATH.length);
    const match = /^FY(\d+)\/Q([1-4])\/Sprint-(\d+)\/(?:index\.html)?$/.exec(relative);
    if (!match) continue;
    const rank = [Number(match[1]), Number(match[2]), Number(match[3])];
    if (!rank.every(value => Number.isSafeInteger(value) && value > 0)) continue;
    const newer =
      !newest ||
      rank[0] > newest.rank[0] ||
      (rank[0] === newest.rank[0] && rank[1] > newest.rank[1]) ||
      (rank[0] === newest.rank[0] && rank[1] === newest.rank[1] && rank[2] > newest.rank[2]);
    if (newer) newest = { rank, path: `${RELEASE_DOCS_PATH}FY${match[1]}/Q${match[2]}/Sprint-${match[3]}/` };
  }
  return newest?.path;
}

export function PublishedReleaseNotesCard() {
  const api = useApiHolder().get(techdocsStorageApiRef);
  const fetchApi = useApiHolder().get(fetchApiRef);
  const footer = useHomeCardFooterStyles();
  const [path, setPath] = useState<string>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      // Either artifact can identify a published release when the other is
      // unavailable (for example, a site with search disabled).
      const [navigation, search] = await Promise.allSettled([
        api ? api.getEntityDocs(RELEASE_DOCS_ENTITY, '') : Promise.resolve(''),
        (async (): Promise<string[]> => {
          if (!api || !fetchApi) return [];
          const storageUrl = await api.getStorageUrl();
          const response = await fetchApi.fetch(`${storageUrl}/default/component/pt-release-notes/search/search_index.json`);
          if (!response.ok) throw new Error(`Published release search responded with HTTP ${response.status}`);
          const index: unknown = await response.json();
          if (!index || typeof index !== 'object' || !('docs' in index) || !Array.isArray(index.docs)) {
            throw new Error('Published release search has an invalid docs list');
          }
          return index.docs.flatMap(item =>
            item && typeof item === 'object' && 'location' in item && typeof item.location === 'string' ? [item.location] : [],
          );
        })(),
      ]);
      if (!cancelled) {
        setPath(
          latestPublishedReleasePath(
            navigation.status === 'fulfilled' ? navigation.value : '',
            window.location.origin,
            search.status === 'fulfilled' ? search.value : [],
          ),
        );
        setLoading(false);
      }
    };
    refresh();
    const timer = setInterval(refresh, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, fetchApi]);
  return (
    <InfoCard
      title="Release notes"
      variant="fullHeight"
      actionsClassName={footer.actions}
      actions={
        <Link to={path ?? RELEASE_DOCS_PATH} className={footer.link}>
          {path ? 'View latest release notes' : 'View release notes'}
        </Link>
      }
    >
      <Typography variant="body2" color="textSecondary">
        {path
          ? 'See what the Platform Engineering team has delivered.'
          : loading
          ? 'Checking the published release notes...'
          : 'The latest release page could not be identified. Browse the published release notes.'}
      </Typography>
    </InfoCard>
  );
}
